export interface IWell {
  id: number;
  name: string;
  status: WellStatuses;
  timeZone: string;
  goStreamWellIds: number[];
}

export interface IWellbore {
  id: number;
  name: string;
  maxTimeLogDepth: number;
  maxTimeLogTime: number;
  wellId: number;
  numLogsTime: number;
  numLogsDepth: number;
}

export interface ICurve {
  id: number;
  mnemonic: string;
  typeExt: string;
  goStreamId: number;
  unit: {
    id: number;
    abbreviation: string;
  };
  parameter: {
    id: number;
    name: string;
    displayMnemonic: string;
    generalName?: string;
    classOfUnitId: number;
  };
}

export interface IWellLog {
  id: number;
  goStreamName: string;
  logName?: {
    id: number;
    name: string;
  };
  index: 'LOG_DEPTH' | 'LOG_TIME';
  curves: Array<ICurve>;
  runNumber?: number[];
  supplier?: {
    id: number;
    name: string;
  };
  service?: {
    id: number;
    name: string;
  };
}

export enum LogsIndex {
  TIME = 'LOG_TIME',
  DEPTH = 'LOG_DEPTH',
}

export enum BaseParameterTypes {
  HOOKLOAD = 'HOOKLOAD',
  STANDPIPE_PRESSURE = 'STANDPIPE_PRESSURE',
  DEPTH_HOLE = 'DEPTH_HOLE',
  ROTARY_TORQUE = 'ROTARY_TORQUE',
  BLOCK_POSITION = 'BLOCK_POSITION',
  DEPTH_BIT = 'DEPTH_BIT',
  MUD_FLOW_IN = 'MUD_FLOW_IN',
  RATE_OF_PENETRATION = 'RATE_OF_PENETRATION',
  WEIGHT_ON_BIT = 'WEIGHT_ON_BIT',
  ROTARY_SPEED = 'ROTARY_SPEED',
  AUTO_RECOGNIZED_EVENTS = 'AUTO_RECOGNIZED_EVENTS',
  AUTO_RECOGNIZED_OPERATIONS = 'AUTO_RECOGNIZED_OPERATIONS',
  MAGNETIC_TOOLFACE = 'MAGNETIC_TOOLFACE',
  GRAVITY_TOOLFACE = 'GRAVITY_TOOLFACE',
  BIT_SPEED = 'BIT_SPEED',
  BIT_CONTACT_PRESSURE = 'BIT_CONTACT_PRESSURE',
  MECH_SPEC_ENERGY_BIT = 'MECH_SPEC_ENERGY_BIT',
  MECH_SPEC_ENERGY_SURFACE = 'MECH_SPEC_ENERGY_SURFACE',
  MUD_FLOW_OUT = 'MUD_FLOW_OUT',
  MUD_FLOW_OUT_PERCENT = 'MUD_FLOW_OUT_PERCENT',
  TOTAL_PUMPS_STROKE_RATE = 'TOTAL_PUMPS_STROKE_RATE',
  SURFACE_ANNULAR_PRESSURE = 'SURFACE_ANNULAR_PRESSURE',
  MUD_DENSITY_IN = 'MUD_DENSITY_IN',
  MUD_DENSITY_OUT = 'MUD_DENSITY_OUT',
  ECD_AT_BIT = 'ECD_AT_BIT',
  ECD_AT_TOTAL_DEPTH = 'ECD_AT_TOTAL_DEPTH',
  RUNNING_SPEED = 'RUNNING_SPEED',
  ACTIVE_TANK_VOLUME = 'ACTIVE_TANK_VOLUME',
  TRIP_TANK_VOLUME = 'TRIP_TANK_VOLUME',
  TOTAL_GAS = 'TOTAL_GAS',
  SURVEY_INC = 'SURVEY_INC',
  SURVEY_AZI = 'SURVEY_AZI',
  SURVEY_AZI_CORR = 'SURVEY_AZI_CORR',
  SURVEY_DOGLEG = 'SURVEY_DOGLEG',
  RESISTIVITY_1 = 'RESISTIVITY_1',
  RESISTIVITY_2 = 'RESISTIVITY_2',
  GAMMA_RAY_1 = 'GAMMA_RAY_1',
  GAMMA_RAY_2 = 'GAMMA_RAY_2',
  POROSITY_1 = 'POROSITY_1',
  POROSITY_2 = 'POROSITY_2',
  THERMAL_NEUTRON_POROSITY = 'THERMAL_NEUTRON_POROSITY',
  NUMBER_OF_JOINTS = 'NUMBER_OF_JOINTS',
}

export enum DefaultMnemonic {
  HOOKLOAD = 'HKLA',
  STANDPIPE_PRESSURE = 'SPPA',
  DEPTH_HOLE = 'DMEA',
  ROTARY_TORQUE = 'TQA',
  BLOCK_POSITION = 'BPOS',
  DEPTH_BIT = 'DBTM',
  MUD_FLOW_IN = 'MFIA',
  RATE_OF_PENETRATION = 'ROPA',
  WEIGHT_ON_BIT = 'WOBA',
  ROTARY_SPEED = 'RPMA',
  AUTO_RECOGNIZED_EVENTS = 'OP',
  AUTO_RECOGNIZED_OPERATIONS = 'KPI',
  MAGNETIC_TOOLFACE = 'SMTF',
  GRAVITY_TOOLFACE = 'SGTF',
  BIT_CONTACT_PRESSURE = 'DBHP',
  MECH_SPEC_ENERGY_SURFACE = 'SMSE',
  MECH_SPEC_ENERGY_BIT = 'DMSE',
  BIT_SPEED = 'BIT_RPM',
  MUD_FLOW_OUT = 'MFOA',
  MUD_FLOW_OUT_PERCENT = 'MFOP',
  TOTAL_PUMPS_STROKE_RATE = 'STRC',
  SURFACE_ANNULAR_PRESSURE = 'CHKP',
  MUD_DENSITY_IN = 'MDIA',
  MUD_DENSITY_OUT = 'MDOA',
  ECD_AT_BIT = 'ECDB',
  ECD_AT_TOTAL_DEPTH = 'ECDT',
  RUNNING_SPEED = 'RSA',
  ACTIVE_TANK_VOLUME = 'TVA',
  TRIP_TANK_VOLUME = 'TTV1',
  TRIP_TANK_VOLUME_ALT = 'TTV2',
  TOTAL_GAS = 'GASA',
  SURVEY_INC = 'SINC',
  SURVEY_AZI = 'SAZU',
  SURVEY_AZI_CORR = 'SAZC',
  SURVEY_DOGLEG = 'SDLS',
  RESISTIVITY_1 = 'MR1',
  RESISTIVITY_1_ALT = 'MR1C',
  RESISTIVITY_2 = 'MR2',
  RESISTIVITY_2_ALT = 'MR2C',
  GAMMA_RAY_1 = 'MG1',
  GAMMA_RAY_1_ALT = 'MG1C',
  GAMMA_RAY_2 = 'MG2',
  GAMMA_RAY_2_ALT = 'MG2C',
  POROSITY_1 = 'MPO1',
  POROSITY_2 = 'MPO2',
  THERMAL_NEUTRON_POROSITY = 'TNPH',
  NUMBER_OF_JOINTS = 'JOINTS_RUN',
}

export interface IBaseParameter {
  wellboreId: number;
  parameter: BaseParameterTypes;
  logType: LogsIndex;
  curveId?: number;
}

export enum WellStatuses {
  ACTIVE_GREEN = 'ACTIVE_GREEN',
  ACTIVE_YELLOW = 'ACTIVE_YELLOW',
  ACTIVE_RED = 'ACTIVE_RED',
  COMPLETED = 'COMPLETED',
  BAD = 'BAD',
}
